import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { RelationalOperator } from '@mtna/data-core-ts';

import { RdsChip } from '../chips/chips.component';
import {
  RDS_DATE_OPERATION_OPTIONS,
  RdsDateFilter,
  RdsDateOperation,
  RdsDateOperationOption,
} from '../core/models/filter/date-filter.model';
import { _rdsVerifyMomentJsExists } from '../core/utils/verify-moment-js-util';

const DEFAULT_SUPPORTED_DATE_FILTER_OPERATIONS: RdsDateOperation[] = ['GT', 'GE', 'EQ', 'NE', 'LT', 'LE'];
@Component({
  selector: 'rds-c-date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: ['YYYY-MM-DD'],
        },
        display: {
          dateInput: 'YYYY-MM-DD',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
        },
      },
    },
  ],
})
export class RdsDateFilterComponent implements OnChanges {
  static ngAcceptInputType_supportedOperators: RelationalOperator[] | null | undefined;

  /** Date Filters */
  @Input() filters: Array<RdsDateFilter> | null | undefined = [];

  /** Whether to use the large chip style variant */
  @HostBinding('class.large-chips')
  @Input()
  largeChips = false;

  /**
   * The supported `RelationalOperator`s. i.e. what operaters does the current data product support.
   * Defaults to 'GT', 'GE', 'EQ', 'NE', 'LT', 'LE'.
   */
  @Input()
  set supportedOperators(operators: RelationalOperator[]) {
    this._supportedOperators = operators || DEFAULT_SUPPORTED_DATE_FILTER_OPERATIONS;
    this.determineSelectOptions();
    this.cdr.markForCheck();
  }
  get supportedOperators(): RelationalOperator[] {
    return this._supportedOperators;
  }
  private _supportedOperators: RelationalOperator[] = DEFAULT_SUPPORTED_DATE_FILTER_OPERATIONS;

  /** Event emitted when the user adds new filter */
  @Output() add = new EventEmitter<RdsDateFilter>();
  /** Event emitted when the user removes a filter */
  @Output() remove = new EventEmitter<RdsDateFilter>();

  /** A record to indicate which options should be enabled based on the current selected filters */
  enabledOptions: Record<RdsDateOperation, boolean> = { GT: true, GE: true, EQ: true, NE: true, LT: true, LE: true };

  /** Select options to choose what type of filter operator to use. */
  filterOptions: ReadonlyArray<RdsDateOperationOption> = [];

  /** Filter from the current control values */
  newValue = new RdsDateFilter(null, null);

  constructor(private cdr: ChangeDetectorRef) {
    // Ensure moment js was added to the application correctly
    _rdsVerifyMomentJsExists();
    // Set initial, default options
    this.determineSelectOptions();
  }

  /** Given the current supported operations, what select options should be visible. */
  determineSelectOptions() {
    let newOptions: RdsDateOperationOption[] = [];
    this.supportedOperators.forEach((operator) => {
      const option: RdsDateOperationOption | undefined = RDS_DATE_OPERATION_OPTIONS.find((o) => o.type === operator);
      if (option) {
        newOptions = [...newOptions, option];
      }
    });
    this.filterOptions = newOptions;
  }

  /**
   * Emit added values and clear the inputs
   */
  handleAdd(filter: RdsDateFilter) {
    this.add.emit(filter);
    // Reset controls
    this.newValue = new RdsDateFilter(null, null);
  }

  handleRemove(chip: RdsChip) {
    if (this.filters) {
      const found = this.filters.find((f) => (!f.operation || !f.value ? false : f.operation.type + f.value.valueOf() === chip.id));
      this.remove.emit(found);
    }
  }

  /**
   * Any time the filters change, determine which operations are still applicable, if any
   */
  ngOnChanges(changes: SimpleChanges) {
    const filtersChange: SimpleChange = changes.filters;
    if (filtersChange) {
      const changedFilters: Array<RdsDateFilter> | null | undefined = filtersChange.currentValue;
      if (changedFilters) {
        // start all as true and flip to false
        this.enabledOptions = { GT: true, GE: true, EQ: true, NE: true, LT: true, LE: true };
        for (const f of changedFilters) {
          switch (f.operation?.type) {
            case 'GT':
            case 'GE':
              this.enabledOptions.GT = false;
              this.enabledOptions.GE = false;
              break;
            case 'LT':
            case 'LE':
              this.enabledOptions.LT = false;
              this.enabledOptions.LE = false;
              break;
          }
        }
        this.cdr.markForCheck();
      }
    }
  }
}
